import { useTranslation } from "react-i18next";
import type { TAnalyticsByHour, TAnalyticsByWeekday } from "@/types/TAnalytics";

type UsageHeatmapProps = {
  brokerByHour: TAnalyticsByHour[];
  brokerByWeekday: TAnalyticsByWeekday[];
  claimByHour: TAnalyticsByHour[];
  claimByWeekday: TAnalyticsByWeekday[];
};

export function UsageHeatmap({ 
  brokerByHour, 
  brokerByWeekday, 
  claimByHour, 
  claimByWeekday 
}: UsageHeatmapProps) {
  const { t } = useTranslation();

  // Combine broker and claim data
  const combinedByHour = brokerByHour.map((b, i) => ({
    hour: b.hour,
    count: b.count + (claimByHour[i]?.count || 0),
  }));

  const combinedByWeekday = brokerByWeekday.map((b, i) => ({
    weekday: b.weekday,
    name: b.name,
    count: b.count + (claimByWeekday[i]?.count || 0),
  }));

  // Find max for color scaling
  const maxHourCount = Math.max(...combinedByHour.map(h => h.count), 1);
  const maxWeekdayCount = Math.max(...combinedByWeekday.map(w => w.count), 1);

  const getHourColor = (count: number) => {
    const intensity = count / maxHourCount;
    if (intensity === 0) return "bg-gray-100 dark:bg-gray-700";
    if (intensity < 0.25) return "bg-blue-100 dark:bg-blue-900/30";
    if (intensity < 0.5) return "bg-blue-200 dark:bg-blue-800/50";
    if (intensity < 0.75) return "bg-blue-400 dark:bg-blue-600";
    return "bg-blue-600 dark:bg-blue-500";
  };

  const getWeekdayColor = (count: number) => {
    const intensity = count / maxWeekdayCount;
    if (intensity === 0) return "bg-gray-100 dark:bg-gray-700";
    if (intensity < 0.25) return "bg-green-100 dark:bg-green-900/30";
    if (intensity < 0.5) return "bg-green-200 dark:bg-green-800/50";
    if (intensity < 0.75) return "bg-green-400 dark:bg-green-600";
    return "bg-green-600 dark:bg-green-500";
  };

  // Group hours for display (show every 3 hours)
  const displayHours = [0, 3, 6, 9, 12, 15, 18, 21];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        {t("analytics.heatmap.title", "Nutzung nach Zeit")}
      </h3>
      
      <div className="space-y-6">
        {/* By Hour */}
        <div>
          <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
            {t("analytics.heatmap.byHour", "Nach Tageszeit")}
          </h4>
          <div className="flex gap-1">
            {combinedByHour.map((h) => (
              <div
                key={h.hour}
                className={`flex-1 h-8 rounded ${getHourColor(h.count)} flex items-center justify-center`}
                title={`${h.hour}:00 - ${h.count} Abfragen`}
              >
                {displayHours.includes(h.hour) && (
                  <span className="text-xs text-gray-600 dark:text-gray-300">{h.hour}</span>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>0:00</span>
            <span>12:00</span>
            <span>23:00</span>
          </div>
        </div>

        {/* By Weekday */}
        <div>
          <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
            {t("analytics.heatmap.byWeekday", "Nach Wochentag")}
          </h4>
          <div className="flex gap-2">
            {combinedByWeekday.map((w) => (
              <div key={w.weekday} className="flex-1 text-center">
                <div
                  className={`h-12 rounded ${getWeekdayColor(w.count)} flex items-center justify-center mb-1`}
                  title={`${w.name}: ${w.count} Abfragen`}
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {w.count}
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{w.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
